import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MAX_PHOTOS, MIN_PHOTOS, PhotoManager } from "@/components/profile/PhotoManager";
import { VideoUploader } from "@/components/experience/VideoUploader";
import { CityField, IntentPicker, InterestPicker } from "@/pages/Apply";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const startPhotos = (u) => [...new Set([...(u.photos || []), ...(u.application_photos || [])])].slice(0, MAX_PHOTOS);

export default function Welcome() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: user.name, home_city: user.home_city, bio: user.bio, interests: user.interests, intents: user.intents, video: user.video || null, photo_focus: user.photo_focus || null, photos: startPhotos(user) });
  const [busy, setBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  if (user.status !== "approved") return <Navigate to="/status" replace />;
  if (user.profile_complete) return <Navigate to="/connect" replace />;

  const save = async (e) => {
    e.preventDefault();
    if (f.photos.length < MIN_PHOTOS) return toast.error(`Please add at least ${MIN_PHOTOS} photos`);
    if (f.home_city.trim().length < 2) return toast.error("Please add your home city");
    setBusy(true);
    try { const { data } = await api.put("/me", f); setUser(data); toast.success("Welcome to the club"); navigate("/connect", { replace: true }); }
    catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="welcome-page">
      <header className="shell flex h-20 items-center border-b border-border"><Logo to="/welcome" /></header>
      <div className="shell max-w-[860px] pt-12">
        <p className="eyebrow mb-3">Membership approved</p>
        <h1 className="font-display text-4xl leading-none sm:text-5xl lg:text-6xl" data-testid="welcome-title">Create your profile, {user.name.split(" ")[0]}.</h1>
        <p className="mt-4 max-w-xl text-foreground/80">Add {MIN_PHOTOS} to {MAX_PHOTOS} photos and a few words about you. Members only ever see your first name.{user.application_photos?.length ? " We've brought in the photos from your application. Keep, reorder or replace them." : ""}</p>
        <form onSubmit={save} className="mt-10 space-y-8" noValidate>
          <PhotoManager value={f.photos} onChange={set("photos")} focus={f.photo_focus} onFocus={set("photo_focus")} />
          <VideoUploader value={f.video} onChange={set("video")} onBusy={setVideoBusy} testId="welcome-video" label="Intro video" addLabel="Add an intro video" />
          <CityField value={f.home_city} onChange={set("home_city")} />
          <IntentPicker value={f.intents} onChange={set("intents")} />
          <InterestPicker value={f.interests} onChange={set("interests")} />
          <div><label htmlFor="w-bio" className="field-label">About you</label><textarea id="w-bio" rows={4} maxLength={600} className="field py-3" value={f.bio} onChange={(e) => set("bio")(e.target.value)} data-testid="welcome-bio-input" /></div>
          <button type="submit" className="btn-primary" disabled={busy || videoBusy || f.photos.length < MIN_PHOTOS} data-testid="welcome-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Complete profile and enter the club"}
          </button>
        </form>
      </div>
    </div>
  );
}
